import { PLP_SIMILAR_DESIGNS_DURATION_MS } from '../../constants';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { shuffleItems } from '@/features/similar-designs/utils/shuffleItems';
import { X } from 'lucide-react';
import { useAppDispatch } from '@/app/store/store';
import { similarDesignsBuild, SimilarDesignCard, type SimilarDesignItem } from '@/features/similar-designs';

interface ProductCardSimilarSectionProps {
  designId: number;
  onClose: () => void;
  onBeforeNavigate: () => void;
  openInNewTab?: boolean;
  compactPinkWishlist?: boolean;
  desktopInline?: boolean;
}

/** Mounted inside the measured product row so expansion moves the following rows down. */
export function ProductCardSimilarSection({ designId, onClose, onBeforeNavigate, openInNewTab, compactPinkWishlist, desktopInline }: ProductCardSimilarSectionProps) {
  const { analytics } = useAnalytics();
  const dispatch = useAppDispatch();
  const { service } = useMemo(() => similarDesignsBuild(dispatch), [dispatch]);
  const [items, setItems] = useState<SimilarDesignItem[] | null>(null);
  const [error, setError] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const didAutoScrollRef = useRef(false);

  useEffect(() => {
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => setRevealed(true));
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    service.getSimilarDesigns(String(designId))
      .then(result => {
        if (!cancelled) setItems(shuffleItems(result.filter(item => item.designId !== designId)));
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [designId, service]);

  useEffect(() => {
    if (!revealed || didAutoScrollRef.current) return;

    const timeout = window.setTimeout(() => {
      didAutoScrollRef.current = true;
      const section = sectionRef.current;
      if (!section) return;

      const viewportBottom = window.visualViewport
        ? window.visualViewport.offsetTop + window.visualViewport.height
        : window.innerHeight;

      if (section.getBoundingClientRect().bottom > viewportBottom) {
        section.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, PLP_SIMILAR_DESIGNS_DURATION_MS);

    return () => window.clearTimeout(timeout);
  }, [revealed]);

  // Match PDP: render no section when the request fails or returns no other designs.
  if (error || items?.length === 0) return null;

  return (
    <div className="mx-[calc(0px-var(--plp-similar-inset,0px))] grid transition-[grid-template-rows,opacity] duration-[var(--similar-duration)] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
      style={{
        '--similar-duration': `${PLP_SIMILAR_DESIGNS_DURATION_MS}ms`,
        gridTemplateRows: revealed ? '1fr' : '0fr',
        opacity: revealed ? 1 : 0,
      } as CSSProperties}>
      <div className="min-h-0 overflow-hidden">
    <section ref={sectionRef} id={`similar-designs-${designId}`} aria-labelledby={`similar-designs-title-${designId}`}
      className="mt-3 rounded-xl bg-gradient-to-b from-[#EAF6FF] to-[#C9DEFC] p-2 text-[#103064]">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 id={`similar-designs-title-${designId}`} className="font-serif text-lg font-semibold">Similar Designs</h2>
        <button type="button" aria-label="Close similar designs" onClick={onClose}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2">
          <X className="h-5 w-5" />
        </button>
      </div>
      {items === null ? (
          <div role="status" aria-label="Loading similar designs" className="grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-4">
            {[0, 1, 2].map(index => <div key={index} className={`${index === 2 ? 'hidden md:block ' : ''}overflow-hidden rounded-xl bg-white motion-safe:animate-pulse`}>
              <div className="aspect-square bg-gray-100" />
              <div className="m-3 h-3 w-3/4 rounded bg-gray-100" />
              <div className="m-3 h-4 w-1/2 rounded bg-gray-100" />
            </div>)}
          </div>
        ) : <div className="flex snap-x snap-proximity gap-2 md:gap-4 overflow-x-auto overscroll-x-contain pb-1">
            {items.map((item, index) => <div key={item.designId} className="w-[calc((100%-0.5rem)/2)] md:w-[calc((100%-2rem)/3)] shrink-0 snap-start">
              <SimilarDesignCard desktopInline={desktopInline} compactPinkWishlist={compactPinkWishlist} item={item} openInNewTab={openInNewTab} onDesignClick={() => {
                analytics?.trackEvent('Similar Designs Selected', {
                  'Product Id': String(designId),
                  'Item Product Id': String(item.designId),
                  'Item Position Number': index + 1,
                  'Similar Design Id': item.designId,
                  'Similar Design Name': item.designName,
                });
                onBeforeNavigate();
              }} />
            </div>)}
          </div>}
    </section>
      </div>
    </div>
  );
}
